import { ethers } from 'ethers';
import { 
  TimeCapsule, 
  CreateCapsuleInput, 
  PaginationParams, 
  PaginatedResponse,
  CapsuleFilters 
} from '../types';
import { LegacyChainClient } from '../core/LegacyChainClient';

const LEGACY_NFT_ABI = [
  'function createCapsule(string memory ipfsHash, uint256 unlockTime, address[] memory authorizedAddresses) public returns (uint256)',
  'function getCapsuleDetails(uint256 tokenId) public view returns (string memory ipfsHash, uint256 unlockTime, address[] memory authorizedAddresses, bool isUnlocked)',
  'function unlockCapsule(uint256 tokenId) public',
  'function addAuthorizedAddress(uint256 tokenId, address newAddress) public',
  'function removeAuthorizedAddress(uint256 tokenId, address addressToRemove) public',
  'function isAuthorized(uint256 tokenId, address user) public view returns (bool)'
];

export class TimeCapsuleService {
  private client: LegacyChainClient;
  private contractAddress: string;

  constructor(client: LegacyChainClient) {
    this.client = client;
    this.contractAddress = process.env.NEXT_PUBLIC_LEGACY_NFT_ADDRESS || '';
  }

  async create(input: CreateCapsuleInput): Promise<TimeCapsule> {
    // Check rate limits
    const { allowed, remaining } = await this.client['rateLimiter'].checkCapsuleLimit(
      this.client.getConfig().apiKey
    );
    
    if (!allowed) {
      throw new Error('Monthly capsule limit reached');
    }

    let ipfsHash: string;
    let encryptionMetadata: any = {};

    // Handle encryption if requested
    if (input.encrypt) {
      const password = input.password || this.client.crypto.generatePassword();
      let contentToEncrypt: string;

      if (input.content instanceof File) {
        contentToEncrypt = await this.fileToBase64(input.content);
      } else if (Array.isArray(input.content)) {
        const files = await Promise.all(input.content.map(f => this.fileToBase64(f)));
        contentToEncrypt = JSON.stringify(files);
      } else {
        contentToEncrypt = input.content;
      }

      const { encrypted, salt, iv } = await this.client.crypto.encrypt(contentToEncrypt, password);
      
      // Upload encrypted content
      ipfsHash = await this.client.ipfs.uploadJSON({
        encrypted,
        salt,
        iv,
        metadata: input.metadata
      }, input.title, {
        type: 'time-capsule',
        encrypted: true
      });

      encryptionMetadata = { encrypted: true, password };
    } else {
      // Upload unencrypted content
      if (input.content instanceof File) {
        ipfsHash = await this.client.ipfs.uploadFile(input.content, {
          type: 'time-capsule',
          title: input.title,
          ...input.metadata
        });
      } else if (Array.isArray(input.content)) {
        // For multiple files, create a JSON manifest
        const hashes = await Promise.all(
          input.content.map(file => this.client.ipfs.uploadFile(file))
        );
        ipfsHash = await this.client.ipfs.uploadJSON({
          files: hashes,
          metadata: input.metadata
        }, input.title);
      } else {
        ipfsHash = await this.client.ipfs.uploadJSON({
          content: input.content,
          metadata: input.metadata
        }, input.title);
      }
    }

    // Create on-chain capsule
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_NFT_ABI, signer);
    
    const unlockTimestamp = Math.floor(input.unlockTime.getTime() / 1000);
    const tx = await contract.createCapsule(
      ipfsHash,
      unlockTimestamp,
      input.authorizedAddresses || []
    );
    
    const receipt = await tx.wait();
    const tokenId = receipt.logs[0].args.tokenId.toString();

    // Update rate limiter
    await this.client['rateLimiter'].incrementCapsuleCount(this.client.getConfig().apiKey);

    // Create capsule object
    const capsule: TimeCapsule = {
      id: tokenId,
      ipfsHash,
      unlockTime: input.unlockTime,
      authorizedAddresses: input.authorizedAddresses || [],
      isUnlocked: false,
      metadata: {
        title: input.title,
        description: input.description,
        type: input.content instanceof File ? 'file' : 'text',
        encrypted: input.encrypt || false
      },
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Store in backend API
    await this.client.makeRequest('POST', '/capsules', capsule);

    // Emit event
    this.client.emit('capsule:created', capsule);

    return { ...capsule, ...encryptionMetadata };
  }

  async get(capsuleId: string): Promise<TimeCapsule> {
    const response = await this.client.makeRequest<TimeCapsule>('GET', `/capsules/${capsuleId}`);
    
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch capsule');
    }

    return response.data;
  }

  async list(params?: PaginationParams & { filters?: CapsuleFilters }): Promise<PaginatedResponse<TimeCapsule>> {
    const queryParams = new URLSearchParams();
    
    if (params) {
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      
      if (params.filters) {
        Object.entries(params.filters).forEach(([key, value]) => {
          if (value !== undefined) {
            queryParams.append(key, value.toString());
          }
        });
      }
    }

    const response = await this.client.makeRequest<PaginatedResponse<TimeCapsule>>(
      'GET',
      `/capsules?${queryParams.toString()}`
    );
    
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch capsules');
    }

    return response.data;
  }

  async unlock(capsuleId: string, password?: string): Promise<any> {
    // Check on-chain status
    const provider = this.client.getProvider();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_NFT_ABI, provider);
    
    const [ipfsHash, unlockTime, , isUnlocked] = await contract.getCapsuleDetails(capsuleId);
    
    if (!isUnlocked && Date.now() < unlockTime * 1000) {
      throw new Error('Capsule is still locked');
    }

    // Unlock on-chain if needed
    if (!isUnlocked) {
      const signer = this.client.getSigner();
      const contractWithSigner = new ethers.Contract(this.contractAddress, LEGACY_NFT_ABI, signer);
      const tx = await contractWithSigner.unlockCapsule(capsuleId);
      await tx.wait();
    }

    // Retrieve content from IPFS
    const content = await this.client.ipfs.retrieveJSON(ipfsHash);
    
    // Decrypt if needed
    if (content.encrypted) {
      if (!password) {
        throw new Error('Password required to decrypt capsule');
      }
      
      const decrypted = await this.client.crypto.decrypt(
        content.encrypted,
        password,
        content.salt,
        content.iv
      );
      
      return JSON.parse(decrypted);
    }

    this.client.emit('capsule:unlocked', capsuleId);
    
    return content;
  }

  async addAuthorizedAddress(capsuleId: string, address: string): Promise<void> {
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_NFT_ABI, signer);
    
    const tx = await contract.addAuthorizedAddress(capsuleId, address);
    await tx.wait();

    // Update backend
    await this.client.makeRequest('POST', `/capsules/${capsuleId}/authorized`, { address });
  }

  async removeAuthorizedAddress(capsuleId: string, address: string): Promise<void> {
    const signer = this.client.getSigner();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_NFT_ABI, signer);
    
    const tx = await contract.removeAuthorizedAddress(capsuleId, address);
    await tx.wait();

    // Update backend
    await this.client.makeRequest('DELETE', `/capsules/${capsuleId}/authorized/${address}`);
  }

  async isAuthorized(capsuleId: string, address: string): Promise<boolean> {
    const provider = this.client.getProvider();
    const contract = new ethers.Contract(this.contractAddress, LEGACY_NFT_ABI, provider);
    
    return contract.isAuthorized(capsuleId, address);
  }

  private async fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }
} 